<template>
  <div id="dt-viewer" ref="el">
    <View :preview-mode="previewMode">
      <div v-if="isShowingStep" class="dt-viewer__slide-stage">
        <Transition :name="slideTransitionName" appear>
          <Slide
            :key="currentNode.id"
            v-model="slideTransitionName"
            :decision-tree="decisionTree"
            :current-node="currentNode"
            :current-node-view="currentNodeView"
            class="dt-viewer__slide"
          />
        </Transition>
      </div>
    </View>
    <div v-if="debugMode">
      <hr />
      <h3>Debug Info Context</h3>
      <code>
        <ul>
          <li>
            <strong>Current Node Type</strong>
            <div>
              <pre>
                {{ currentNode.name }}
              </pre>
            </div>
          </li>
          <li v-if="isAllowBooking">
            <strong>Final Visit Type</strong>
            <div>
              <pre>
                {{ JSON.stringify(decisionTree.bookingVisitType) }}
              </pre>
            </div>
          </li>
          <li v-for="(value, key) in decisionTree.context" :key="key">
            <strong>{{ key }}</strong>
            <div>
              <pre>
                {{ JSON.stringify(value) }}
              </pre>
            </div>
          </li>
        </ul>
      </code>
    </div>
  </div>
</template>

<script setup>
import { useDecisionTreeStore } from "#/decision_tree_viewer/store/decision-tree";

import View from "#/decision_tree_viewer/components/View.vue";
import Slide from "#/decision_tree_viewer/components/Slide.vue";

import StartNode from "#/decision_tree_viewer/components/Node/StartNode.vue";
import InfoNode from "#/decision_tree_viewer/components/Node/InfoNode.vue";
import SchedulingNoteNode from "#/decision_tree_viewer/components/Node/SchedulingNoteNode.vue";
import SelectPatientTypeNode from "#/decision_tree_viewer/components/Node/SelectPatientTypeNode.vue";
import AgeValidationNode from "#/decision_tree_viewer/components/Node/AgeValidationNode.vue";
import SelectVisitTypeNode from "#/decision_tree_viewer/components/Node/SelectVisitTypeNode.vue";
import SelectStateNode from "#/decision_tree_viewer/components/Node/SelectStateNode.vue";
import QuestionNode from "#/decision_tree_viewer/components/Node/QuestionNode.vue";
import ContinueNode from "#/decision_tree_viewer/components/Node/ContinueNode.vue";
import DenyNode from "#/decision_tree_viewer/components/Node/DenyNode.vue";
import SendToPortalNode from "#/decision_tree_viewer/components/Node/SendToPortalNode.vue";
import MissingVisitTypeNode from "#/decision_tree_viewer/components/Node/MissingVisitTypeNode.vue";

import { historyEntry, isForeignEntry, isOwnEntry } from "#/decision_tree_viewer/history-entry";
import { buildProgressDetail } from "#/decision_tree_viewer/utils/progress-detail";

import { ref, computed, watch, onMounted, onUnmounted } from "vue";

defineProps({
  previewMode: {
    type: Boolean,
    default: false,
  },
});

const nodeComponents = {
  StartNode,
  InfoNode,
  SchedulingNoteNode,
  SelectPatientTypeNode,
  AgeValidationNode,
  SelectVisitTypeNode,
  SelectStateNode,
  QuestionNode,
  ContinueNode,
  DenyNode,
  SendToPortalNode,
  MissingVisitTypeNode,
};

const el = ref(null);

const slideTransitionName = ref("");

const { decisionTree, debugMode } = useDecisionTreeStore();

const currentNode = computed(() => decisionTree.currentNode);
const currentNodeView = computed(() => {
  const nodeName = currentNode.value.name;

  if (!(nodeName in nodeComponents)) {
    throw new Error(`Unknown node type: ${nodeName}`);
  }

  return nodeComponents[nodeName];
});

const isShowingStep = computed(() => decisionTree.isShowingStep);
const isAllowBooking = computed(() => decisionTree.isAllowBooking);
const isDeny = computed(() => decisionTree.isDeny);
const isSendToPortal = computed(() => decisionTree.isSendToPortal);

function dispatchProgress(currNode = currentNode.value, prevNode = null) {
  const detail = buildProgressDetail({
    currNode,
    prevNode,
    previousNodes: decisionTree.previousNodes,
    progressPercent: decisionTree.progressPercent,
    currentState: decisionTree.currentState,
  });

  el.value.dispatchEvent(new CustomEvent("decision-tree:progress", { bubbles: true, detail }));
}

// Subsequent node changes dispatch progress here; the first node is dispatched
// from onMounted instead — the viewer now mounts after start() (currentNode
// already set), so a plain watcher wouldn't fire for it.
watch(currentNode, dispatchProgress);

watch(isAllowBooking, async (newValue) => {
  if (!newValue) {
    return;
  }

  const event = new CustomEvent("decision-tree:allow-booking", {
    bubbles: true,
    detail: {
      patientType: decisionTree.bookingPatientType,
      visitTypeId: decisionTree.bookingVisitTypeId,
      switchProviderGroupIds: decisionTree.bookingProviderGroupIds,
      ageRange: decisionTree.bookingAgeRange,
      currentState: decisionTree.currentState,
      reviewStepBroadcasts: decisionTree.reviewStepBroadcasts,
      bookingMetadata: {
        confirmationPageBroadcasts: decisionTree.confirmationPageBroadcasts,
        schedulingNotes: decisionTree.schedulingNotes,
      },
    },
  });

  el.value.dispatchEvent(event);
});

watch(isDeny, async (newValue) => {
  if (!newValue) {
    return;
  }

  const event = new CustomEvent("decision-tree:deny", {
    bubbles: true,
    detail: {
      currentNodeId: decisionTree.currentNodeId,
    },
  });

  el.value.dispatchEvent(event);
});

watch(isSendToPortal, async (newValue) => {
  if (!newValue) {
    return;
  }

  const event = new CustomEvent("decision-tree:send-to-portal", {
    bubbles: true,
    detail: {
      currentNodeId: decisionTree.currentNodeId,
    },
  });

  el.value.dispatchEvent(event);
});

function onPopState(event) {
  const { state } = event;

  if (isOwnEntry(state)) {
    if (decisionTree.prevNodeId === state.currentNodeId) {
      slideTransitionName.value = "slide-prev";
      decisionTree.goToPrevNode();
    } else if (decisionTree.nextNodeId === state.currentNodeId) {
      slideTransitionName.value = "slide-next";
      decisionTree.goToNextNode();
    }
  } else if (isForeignEntry(state)) {
    history.replaceState(historyEntry(decisionTree.currentNodeId), null);
  } else if (decisionTree.nextNodeId) {
    // Handle transition to next step of ABW
    decisionTree.goToNextNode();
  }
}

onMounted(() => {
  // A reload keeps the previous document's entry state, so the entry is rewritten to the node on screen.
  history.replaceState(historyEntry(decisionTree.currentNodeId), null);

  // Dispatch progress for the first node; el is bound and currentNode is set by
  // the time onMounted runs (the viewer mounts after start()).
  dispatchProgress();

  window.addEventListener("popstate", onPopState);
});

onUnmounted(() => {
  window.removeEventListener("popstate", onPopState);
});
</script>

<style lang="scss" scoped>
.dt-viewer__slide-stage {
  display: grid;
  justify-items: center;
  width: 100%;
}

.dt-viewer__slide {
  grid-area: 1 / 1;
}
</style>
